import React from 'react';
import Tippy from '@tippy.js/react';
import { subscriptionStatus } from './constants';
import { formatDate } from 'helpers/dateTimeFormatter';
import { uniq, map } from 'lodash';
import { ASSET_TYPES } from 'helpers/constants';
import { getAssetIcon } from 'helpers/icons';
import { translate } from 'helpers/translateHelper';
import { InfoButtonWithOnlineHelp } from 'sharedComponents/InfoButtonWithOnlineHelp';
import { routes } from 'routes';


export const getSearchMatcher = (search) => {
    const searchToLower = search?.toString()?.toLowerCase();
    return (value) => value?.toString()?.toLowerCase()?.includes(searchToLower);
};

const convertToTranslation = (value) => {
    if (value && typeof value === 'string') {
        console.log(`Converting translation for value: ${value}`);
        return translate(`ABB.Powertrain.Frontend.${value.toLowerCase()}Label`) || value;
    }
};

export const getSubscriptionStatusToggleOptions = (translate) => [
    { label: translate('ABB.Powertrain.Frontend.ptToolsSubscriptionStatusTab_Active_subscription'), value: subscriptionStatus.ACTIVE },
    { label: translate('ABB.Powertrain.Frontend.ptToolsSubscriptionStatusTab_Expired_subscription'), value: subscriptionStatus.EXPIRED }
];

// eslint-disable-next-line import/no-unused-modules
export const getAssesTypeOptions = (contracts, activeTab) => {
    let filteredContracts = [];

    if (activeTab === subscriptionStatus.ACTIVE) {
        filteredContracts = contracts?.filter(
            contract =>
                contract?.typeName &&
                contract?.typeName !== '' &&
                contract?.typeName !== subscriptionStatus.FREE &&
                contract?.typeName !== subscriptionStatus.FREE_FOR_RENEWAL
        );
    } else if (activeTab === subscriptionStatus.EXPIRED) {
        filteredContracts = contracts?.filter(
            contract =>
                contract?.typeName && contract?.typeName === subscriptionStatus.FREE ||
                contract?.typeName === subscriptionStatus.FREE_FOR_RENEWAL &&
                contract?.previousSubscription?.prevType &&
                contract?.previousSubscription?.prevType !== subscriptionStatus.FREE_FOR_RENEWAL
        );
    }

    // Get unique asset types from filtered contracts
    const contractTypes = map(
        uniq(
            map(filteredContracts, 'type').filter(type => type !== '')
        ),
        type => type?.toUpperCase()
    );

    const assetTypeIds = map(contractTypes, contractType =>
        ASSET_TYPES[contractType]
    );

    return contractTypes?.map((contractType, index) => {
        const assetTypeID = assetTypeIds[index];

        return {
            label:
                <>
                    {getAssetIcon(assetTypeID, { width: 16, height: 16 })}{' '}
                    {translate(`ABB.Powertrain.Frontend.${contractType?.toLowerCase()}Label`)}
                </>
            ,
            value: contractType?.toLowerCase(),
        };
    });
};

// eslint-disable-next-line import/no-unused-modules
export const getSubscriptionTypeOptions = (contracts, activeTab) => {
    let contractTypes = [];

    if (activeTab === subscriptionStatus.ACTIVE) {
        contractTypes = uniq(
            contracts
                ?.filter(contract => contract?.typeName && contract?.typeName !== '' && contract?.typeName !== subscriptionStatus.FREE && contract?.typeName !== subscriptionStatus.FREE_FOR_RENEWAL)
                .map(contract => contract.typeName)
        );
    } else if (activeTab === subscriptionStatus.EXPIRED) {
        contractTypes = uniq(
            contracts
                ?.filter(contract =>
                    contract?.typeName && contract?.typeName === subscriptionStatus.FREE_FOR_RENEWAL || contract?.typeName === subscriptionStatus.FREE ||
                    contract?.previousSubscription?.prevType && contract?.previousSubscription?.prevType !== subscriptionStatus.FREE_FOR_RENEWAL
                )
                .map(contract =>
                    contract?.typeName === subscriptionStatus.FREE_FOR_RENEWAL
                        ? contract?.previousSubscription?.prevType
                        : contract?.typeName
                )
                .filter(typeName => typeName && typeName !== subscriptionStatus.FREE_FOR_RENEWAL)
        );
    }

    return contractTypes?.map((contractType) => ({
        label: <>{contractType}</>,
        value: contractType?.toLowerCase(),
    }));
};

const onAssetClick = (item, assets, asset, history) => {
    const assetId = asset?.id;
    history.push({
        pathname: routes.ConditionOverviewV2,
        search: new URLSearchParams({ assetId }).toString(),
        state: {
            routeName: translate('ABB.Powertrain.Frontend.ptToolsSubscriptionStatus'),
            routeData: {
                asset,
                assets
            }
        }
    });
};

const getFormattedInfoTooltip = (item) => {
    const featureCodesCell = <div>
        {

            <InfoButtonWithOnlineHelp infoText={item?.length > 0 ? item?.join(', ') : translate('ABB.Powertrain.Frontend.noInformationAvailableLabel')} withoutLink />
        }
    </div>;
    return featureCodesCell;
};

export const getSubscriptionsTableColumnConfig = (translate, assets, activeTab, history) => {
    return [
        {
            title: translate('ABB.Powertrain.Frontend.assetNameLabel'),
            dataIndex: 'componentName',
            width: 150,
            isSorting: true,
            render: (item, asset) => <button type='button' className='asset-item' onClick={() => { onAssetClick(item, assets, asset, history); }}>
                {item?.length ?
                    <Tippy content={item} theme='light-border'>
                        <span className='asset-item-name'>{item}</span>
                    </Tippy>
                    :
                    <span>-</span>
                }
            </button>
        },
        {
            title: translate('ABB.Powertrain.Frontend.assetSiNo'),
            dataIndex: 'serialNumber',
            width: 100,
            isSorting: true,
            render: (item) => item ? item : '-'
        },
        {
            title: translate('ABB.Powertrain.Frontend.assetDetailsAssetTypeLabel'),
            dataIndex: 'type',
            width: 100,
            isSorting: true,
            render: (item) => item ? convertToTranslation(item) : '-'
        },
        {
            title: translate('ABB.Powertrain.Frontend.subscriptionType'),
            dataIndex: activeTab === 'expired' ? 'prevType' : 'typeName',
            width: 100,
            isSorting: true,
            render: (item, record) => {
                if (activeTab === 'expired') {
                    if (record?.typeName === subscriptionStatus.FREE_FOR_RENEWAL) {
                        return record?.previousSubscription?.prevType ?? '-';
                    }
                    if (record?.typeName === subscriptionStatus.FREE) {
                        return record?.typeName ?? '-';
                    }
                }
                return item ?? '-';
            }
        },
        {
            title: translate('ABB.Powertrain.Frontend.featureCode'),
            dataIndex: 'featureCodes',
            width: 100,
            isSorting: true,
            render: (item) => getFormattedInfoTooltip(item)
        },
        {
            title: translate('ABB.Powertrain.Frontend.endDate'),
            dataIndex: 'endDate',
            width: 100,
            isSorting: true,
            render: (item) => item ? formatDate(item) : '-'
        }
    ];
};
